import { ActionSheet } from "@capacitor/action-sheet";
import { AppLauncher } from "@capacitor/app-launcher";
import { Capacitor } from "@capacitor/core";
import { type CSSObject } from "@emotion/react";
import { IonText } from "@ionic/react";
import { useHistory } from "react-router-dom";
import { SubDisinterestedModal } from "src/components/molecules/SubDisinterestedModal";
import { getBonusDetails } from "src/utils/bonus-pay";
import { getFeatureFlags } from "src/utils/feature-flags";
import { Card, Colors, fontSmall, Icon, useFeatureFlags, useModal } from "swing-components";
import { translateRequestSubjectData } from "swing-utils";

import { TooltipTriggerSubAvailability } from "~components";
import { createRequestUrl } from "~pages";
import {
  calcDifferenceFromNow,
  formatCityStateZip,
  formatDisplayTimes,
  formatPay,
  formatStringArray,
  generateGoogleMapsLink,
  handleMapLinkNavigation,
  OpeningsData,
  openLinkInApp,
  translate,
  useIsDesktop,
} from "~utils";

/********* Styles ********/
const styles = {
  dateRow: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    fontWeight: 600,
    fontSize: 14,
    textWrap: "pretty",
  },
  truncateTwoLines: {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "normal",
    lineClamp: 2,
    WebkitLineClamp: 2,
    maxWidth: 256,
    "@media (min-width: 900px)": {
      maxWidth: "none",
    },
  },
  truncateOneLine: {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "normal",
    lineClamp: 1,
    WebkitLineClamp: 1,
    maxWidth: 256,
    "@media (min-width: 900px)": {
      maxWidth: "none",
    },
  },
  infoRow: {
    display: "flex",
    gap: 16,
    margin: "8px auto",
  },
  dateContainer: {
    flexWrap: "wrap",
    alignItems: "flex-start",
    containerType: "inline-size",
    "@container (max-width: 300px)": {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  payDescriptorW2: {
    margin: 0,
    color: Colors.green500,
    fontSize: 14,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
} satisfies Record<string, CSSObject>;

export type OpeningsCardProps = {
  request: OpeningsData;
  /** Pay Clarity feature flag */
};

export function OpeningsCard(props: OpeningsCardProps) {
  const { request } = props;

  // TODO: why is there a flags and a getFeatureFlags()?
  const { flags } = useFeatureFlags();
  const history = useHistory();

  /********* Constants ********/
  const requestDuration = request.intervals.length;
  const requestAge = calcDifferenceFromNow(request.createdAt);
  const FLEX_FILL_ELIGIBILITY_TIME_IN_MINS = 240;
  // TODO: why isn't all this feature's enablement logic consolidated into a single function
  // and available to other components?
  const flexFillEnabled =
    getFeatureFlags().FLEXIBLE_MD_FILL && requestAge > FLEX_FILL_ELIGIBILITY_TIME_IN_MINS;

  // Format grades and subjects
  const { grades, subjects } = formatGradesAndSubjects(
    request.grades ?? [],
    request.subjects ?? [],
  );

  // Disinterested Modal Helpers

  const isDisinterestedEnabled = !request?.hasFitFeedback && flags?.subDisinterestedFeedback;
  const isDesktop = useIsDesktop();
  const { openModal, closeModal } = useModal({
    component: <SubDisinterestedModal onDismiss={() => closeModal()} requestId={request.id} />,
    modalOptions: {
      cssClass: isDesktop ? undefined : "action-sheet-modal",
      breakpoints: isDesktop ? undefined : [0, 1],
      initialBreakpoint: isDesktop ? undefined : 1,
      expandToScroll: true,
    },
  });

  const { hasRequestBonus, bonusText, bonusHourlyPay } = getBonusDetails(request.payment);
  const requestBonusText = `${bonusText} extra pay`;

  /********* Render ********/
  return (
    <Card onClick={() => history.push(createRequestUrl(request.id))}>
      <div
        css={{
          paddingBottom: 8,
          gap: 16,
          margin: "8px auto",
          gridTemplateColumns: flexFillEnabled ? "1fr max-content" : "24px 1fr max-content",
          alignItems: "start",
          display: "flex",
          flexDirection: "row",
        }}
      >
        <div
          css={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          {hasRequestBonus && (
            <div
              css={{
                backgroundColor: "var(--swing-green500)",
                width: "fit-content",
                padding: "0px 4px",
                marginBottom: "2px",
                borderRadius: "4px",
                fontFamily: "var(--swing-font-family-base)",
                fontSize: "var(--swing-font-xs)",
                fontWeight: "var(--swing-font-semibold)",
                color: Colors.white200,
              }}
            >
              {requestBonusText}
            </div>
          )}
          <div
            css={{
              display: "flex",
              flexDirection: "row",
            }}
          >
            {!flexFillEnabled && (
              <Icon name={request.isMultiDay ? "Calendar Overlap" : "Calendar"} />
            )}

            <div>
              <span css={styles.dateRow}>{request.displayDate}</span>
              <div
                css={[
                  /* We want to make sure that the "Available some days" label drops
                       to the third line if pay clarity is enabled, see:
                       https://www.figma.com/design/TWbOTSsp67QRjyzjvEcHuB/W2-Pay-Clarity?node-id=5073-9878&m=dev
                       */
                  { display: "flex", gap: 2, flexDirection: "column" },
                  flexFillEnabled && styles.dateContainer,
                ]}
              >
                <span
                  css={{
                    color: Colors.black,
                    fontSize: 12,
                    marginRight: 8,
                    whiteSpace: "initial",
                    textWrap: "pretty",
                  }}
                >
                  {requestDuration}
                  {request.isMultiDay ? (
                    <> {translate("OPENINGS_CARD_DATE_MULTI_DAY")}</>
                  ) : (
                    <>
                      {" "}
                      {translate("OPENINGS_CARD_DATE_SINGLE_DAY")}
                      {", "}
                      {formatDisplayTimes(request.displayTime || "")}
                    </>
                  )}
                </span>
                {request.isMultiDay && flexFillEnabled && (
                  <div css={{ fontSize: 12 }}>
                    <TooltipTriggerSubAvailability id={`tooltip-${request.id}`} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div
          css={{
            textAlign: "right",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignSelf: "start",
            marginLeft: "auto",
          }}
        >
          {request.payment && (
            <Pay
              payment={{
                ...request.payment,
                bonusHourlyPay,
                hasRequestBonus,
              }}
            />
          )}
        </div>
      </div>
      <div
        css={{
          width: "100%",
          height: 1,
          margin: "8px 0",
          paddingRight: 16,
          backgroundColor: Colors.slate300,
        }}
      />
      <div>
        <div css={styles.infoRow}>
          <div>
            <span
              css={[
                styles.truncateTwoLines,
                {
                  fontWeight: 600,
                  fontSize: 14,
                },
              ]}
            >
              {request.school.name}
            </span>
            {request.missingSkillbuilderAttrs && request.missingSkillbuilderAttrs.length > 0 && (
              <div css={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Icon name="Info" color={Colors.yellow600} />
                <span css={{ ...fontSmall() }}>Required training</span>
              </div>
            )}
            {request.school.address && (
              <a
                css={{
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  zIndex: 2,
                  fontSize: 12,
                  width: "fit-content",
                }}
                target="_blank"
                rel="noreferrer noopener"
                href={generateGoogleMapsLink(request.school.address)}
                onClick={(e) =>
                  handleMapLinkNavigation(e, {
                    getPlatform: Capacitor.getPlatform,
                    appLauncher: AppLauncher,
                    actionSheet: ActionSheet,
                    openLinkInApp: openLinkInApp,
                    query: encodeURIComponent(
                      `${request.school.address?.street} ${request.school.address?.city}, ${request.school.address?.state}, ${request.school.address?.zip}`,
                    ),
                    link: generateGoogleMapsLink({ ...request.school.address }),
                  })
                }
              >
                {request.school.address.street && (
                  <IonText css={styles.truncateOneLine}>{request.school.address.street}</IonText>
                )}
                <IonText css={styles.truncateOneLine}>
                  {formatCityStateZip(request.school.address)}
                </IonText>
              </a>
            )}
          </div>
        </div>
        <div
          css={{
            marginBottom: 0,
            fontSize: 14,
            marginLeft: 0,
            marginTop: 0,
            color: Colors.slate600,
          }}
        >
          <p css={[styles.truncateOneLine, { lineHeight: "24px", margin: 0, maxWidth: "none" }]}>
            {grades}
          </p>
          <p css={[styles.truncateOneLine, { lineHeight: "24px", margin: 0, maxWidth: "none" }]}>
            {subjects}
          </p>
        </div>
        {isDisinterestedEnabled && (
          <div css={{ display: "flex", justifyContent: "end" }}>
            <button
              onClick={(e) => {
                openModal();
                e.stopPropagation();
              }}
              css={{
                display: "flex",
                background: "var(--swing-blue200)",
                border: "solid 1px var(--swing-slate200)",
                padding: 4,
                fontSize: 12,
                gap: 10,
                alignItems: "center",
                borderRadius: 8,
              }}
            >
              <Icon name="Thumb Down" />
              {translate("SUB_DISINTERESTED_BUTTON_LABEL")}
            </button>
          </div>
        )}
      </div>
    </Card>
  );
}

/********* Helper Functions ********/
const formatOpeningsPay = (pay: string) => {
  return pay.replace("/hr", "");
};

export function formatGradesAndSubjects(grades: string[], subjects: string[]) {
  // grades - selected
  let g = formatStringArray(grades);

  // Prioritize SPED (Special Education) by moving it to the front of the array
  const subjectsCopy = [...subjects];
  const spedIndex = subjectsCopy.findIndex((s) => s.trim().toUpperCase() === "SPED");
  if (spedIndex !== -1) {
    const [sped] = subjectsCopy.splice(spedIndex, 1);
    subjectsCopy.unshift(sped);
  }

  // Translate and format subjects
  let s = formatStringArray(translateRequestSubjectData({ subjects: subjectsCopy }));

  // grades - not selected
  if (g.toLowerCase() === "") {
    g = translate("GRADE_NOT_PROVIDED");
  }

  // grades - unknown
  if (g.toLowerCase() === "unknown") {
    g = translate("GRADE_UNKNOWN");
  }

  // subjects - not selected
  if (s.toLowerCase() === "") {
    s = translate("SUBJECT_NOT_PROVIDED");
  }

  // subjects - unknown
  if (s.toLowerCase() === "unknown") {
    s = translate("SUBJECT_UNKNOWN");
  }

  return { grades: g, subjects: s };
}

type PayProps = {
  payment: OpeningsData["payment"] & {
    bonusHourlyPay?: number;
    hasRequestBonus?: boolean;
  };
  /** Pay Clarity feature flag */
};

function Pay(props: PayProps) {
  const { payment } = props;

  if (!payment) {
    return null;
  }

  if (payment.isHourly) {
    return (
      <>
        {payment?.totalPayValue && payment?.totalPayValue > 0 && (
          <p css={styles.payDescriptorW2}>
            {formatPay(false, payment.totalPayValue)} {translate("LABEL_ESTIMATED_TOTAL_SHORT")}
          </p>
        )}
        {payment.hourlyPayValue && payment.hourlyPayValue > 0 && (
          <>
            <p css={[styles.payDescriptorW2, { fontSize: 12, fontWeight: 400 }]}>
              {formatPay(
                true,
                payment.bonusHourlyPay ? payment.bonusHourlyPay : payment.hourlyPayValue,
              )}
            </p>
            {payment.hasRequestBonus && payment.bonusHourlyPay && (
              <p css={[styles.payDescriptorW2, { fontSize: 11, fontWeight: 200 }]}>
                <s>{formatPay(true, payment.hourlyPayValue)}</s>
              </p>
            )}
          </>
        )}
      </>
    );
  }

  return (
    <>
      <IonText
        css={{
          ...styles.dateRow,
          color: Colors.green500,
          fontWeight: 600,
        }}
      >
        {formatOpeningsPay(formatPay(payment.isHourly, payment.payValue))}
      </IonText>
      <span css={{ fontSize: 12, color: Colors.green500 }}>
        {translate("OPENINGS_CARD_PAY_NON_HOURLY")}
      </span>
    </>
  );
}
